import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Typography, Card, CardContent, IconButton, Box, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import formatDate from '../../utils/formatDate';

export default function GeneratedListItem({
  item,
  selected,
  onSelect,
  onDelete,
  onDownload,
  onRename,
}) {
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(item.name);

  const handleEditStart = (e) => {
    e.stopPropagation();
    setNameValue(item.name);
    setEditing(true);
  };

  const handleEditConfirm = (e) => {
    e.stopPropagation();
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== item.name) {
      onRename?.(item, trimmed);
    }
    setEditing(false);
  };

  const handleEditCancel = (e) => {
    e.stopPropagation();
    setNameValue(item.name);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleEditConfirm(e);
    if (e.key === 'Escape') handleEditCancel(e);
  };

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: 1,
        background: selected ? '#e0e0e0' : 'inherit',
        cursor: editing ? 'default' : 'pointer',
      }}
      onClick={() => !editing && onSelect(item)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
        <VisibilityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
      </Box>
      <CardContent sx={{ flex: 1, padding: '8px 0' }}>
        {editing ? (
          <TextField
            autoFocus
            size="small"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            variant="standard"
            fullWidth
            inputProps={{ style: { fontWeight: 500 } }}
          />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {item.name}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary">
          {formatDate(new Date(item.created_at))}
        </Typography>
      </CardContent>

      {editing ? (
        <>
          <IconButton size="small" aria-label="confirm-rename" onClick={handleEditConfirm}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="cancel-rename" onClick={handleEditCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton
            sx={{ marginRight: 0 }}
            edge="end"
            aria-label="rename"
            onClick={handleEditStart}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            sx={{ marginRight: 0 }}
            edge="end"
            aria-label="download"
            onClick={(e) => {
              e.stopPropagation();
              onDownload?.(item);
            }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            sx={{ marginRight: 0 }}
            edge="end"
            aria-label="delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      )}
    </Card>
  );
}

GeneratedListItem.propTypes = {
  item: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onRename: PropTypes.func,
};
